import { LinkToNewTab } from "../../../../../Elements/Link/Link.js";
import GetTransformedDate from "../../../../../../utils/dateViewTransformer.tsx";
import { useContent } from "../../../../../../contexts/contentContext/contentContext.js";
import { useUser } from "../../../../../../contexts/userContext/userContext.js";
import Image from "components/Elements/Image/Image.tsx";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import { useToasts } from "react-toast-notifications";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import Icon from "components/Elements/Icon/Icon.tsx";
import config from "config";
import useCurrentCurrency from "components/Pages/Plans/Utils/useCurrentCurrency.ts";
import { currencies, plansList } from "components/Pages/Plans/Utils/Lists.js";

const UserSubscriptionDetailsCard = ({
  userData,
  setShow,
  setUserIsAutoRenew,
  userIsAutoRenew,
}) => {
  const {
    isPremium,
    isPro,
    isSubscribed,
    isBusiness,
    getUserBillingDetails,
    getUserData,
  } = useUser();
  const { addToast } = useToasts();
  const billingDetails = getUserBillingDetails();
  const { getTextByKey, getLanguage } = useContent();
  const currentCurrency = useCurrentCurrency();
  const subscriberTypeIcon = isSubscribed()
    ? isPremium()
      ? "premium"
      : isPro()
      ? "pro"
      : isBusiness
      ? "business"
      : ""
    : "free";

  const nextBillingDate = userIsAutoRenew
    ? billingDetails?.nextPaymentDate
    : billingDetails?.endDate;
  const isMonthly = billingDetails?.period === 1;

  const getCurrentPlanPrices = () => {
    const plan = plansList.find(
      (plan) => plan.title === userData.userSubscriberSystemName?.toLowerCase()
    );

    if (!plan) return { monthly: 0, yearly: 0 };

    const currencyMap = {
      ILS: { monthly: plan.shekelPriceMonthly, yearly: plan.shekelPriceYearly },
      USD: { monthly: plan.dollarPriceMonthly, yearly: plan.dollarPriceYearly },
      EUR: { monthly: plan.euroPriceMonthly, yearly: plan.euroPriceYearly },
    };

    return currencyMap[currentCurrency] || { monthly: 0, yearly: 0 };
  };

  const currentPeriodPrice = (isMonthly) => {
    const prices = getCurrentPlanPrices();
    return isMonthly ? prices.monthly : prices.yearly;
  };

  const renewSubscriptionButton = () => {
    if (userIsAutoRenew) {
      setShow(true);
      return;
    }
    if (
      // If transaction was cancelled from PayPal side
      billingDetails.status === "INACTIVE" ||
      // or if user comes from old billing system
      billingDetails?.payment_gateway === "WIRE" ||
      (!billingDetails?.token_id && !billingDetails?.provider_id)
    ) {
      window.location.href =
        config.baseurl +
        `/${getLanguage()}/purchase/${billingDetails.type}/${
          billingDetails?.period !== 1 ? "yearly" : "monthly"
        }/`;
      return;
    }
    // user is free subscription sent him to purcahse page to extend his subscription
    if (userData.billingDetails.pricePerPeriod === "0") {
      // redirect to purchase page by billingDetails.type by language as well

      window.location.href =
        config.baseurl +
        `/${getLanguage()}/purchase/${billingDetails.type}/monthly/`;
    } else {
      userIsAutoRenew ? setShow(true) : renewSubscription();
    }
  };

  const renewSubscription = async () => {
    const body = {
      UserId: userData.UserId,
      Renew_Subscription: true,
    };

    const response = await apiRequest(
      "Users",
      "UserRenewBilling",
      body,
      "POST"
    );
    if (response.status === "success") {
      if (response.data) {
        setUserIsAutoRenew(true);
        addToast(getTextByKey("renew-subscription-success"), {
          appearance: "success",
          autoDismiss: true,
        });
      } else {
        addToast(getTextByKey("renew-subscription-failed"), {
          appearance: "error",
          autoDismiss: true,
        });
      }
    } else {
      addToast(getTextByKey("renew-subscription-failed"), {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };
  return (
    <div className="user-subscription-details">
      <div className="first-section">
        <div className="plan-title-div">
          {userData.IsSubUser ? (
            userData.TopUserFullName && (
              <div className="top-user-name">
                <span>{getTextByKey("under-organization")} </span>
                <span className="organization-name">
                  #{userData?.Organization || userData.TopUserFullName}
                </span>
                <Tooltip
                  tooltipText={
                    <span>
                      {getTextByKey("owned-by")} {userData.TopUserFullName}
                      <br />
                      {userData.TopUserEmail}
                    </span>
                  }
                >
                  <Icon name="question-circle" />
                </Tooltip>
              </div>
            )
          ) : (
            <>
              <div className="change-plan">
                {`${getTextByKey("profileuser-your-plan")} `}
                <span className="plan-title">
                  {isSubscribed()
                    ? userData.Subscription
                    : getTextByKey("free-user")}
                </span>
                {isSubscribed() ? (
                  <span>
                    ({currencies[currentCurrency]}
                    {currentPeriodPrice(isMonthly)}/
                    {getTextByKey(isMonthly ? "monthly" : "yearly")})
                  </span>
                ) : null}
              </div>
              {isSubscribed() && (
                <LinkToNewTab page={"plans"} classes="plans-link" isNewTab>
                  {getTextByKey("profileuser-change-plan-link")}
                </LinkToNewTab>
              )}
            </>
          )}
        </div>
        <div className="">
          <Image
            name={`plans/${subscriberTypeIcon}`}
            ext="svg"
            alt=""
            title=""
          />
        </div>
      </div>
      <div className="user-dates-details">
        <div className="date-table">
          <div className="date-sub-title">
            {getTextByKey("profileuser-registration-date")}
          </div>
          <div className="date">
            <GetTransformedDate
              date={userData.RegisterationDate}
              fullView={true}
              showDay={false}
            />
          </div>
        </div>
        {userData.IsSubscribed && (
          <div className="date-table">
            <div className="date-sub-title">
              {getTextByKey("profileuser-subscription-date-creation")}
            </div>
            <div className="date">
              <GetTransformedDate
                date={
                  billingDetails && billingDetails.startDate
                    ? billingDetails.startDate
                    : userData.RegisterationDate
                }
                fullView={true}
                showDay={false}
              />
            </div>
          </div>
        )}
        {isSubscribed() && !getUserData()?.IsSubUser && (
          <div className="date-table">
            <div className="date-sub-title">
              {getTextByKey(
                userIsAutoRenew
                  ? "next-billing-date"
                  : "profileuser-account-subscription-end"
              )}
            </div>
            <div className="date">
              <GetTransformedDate
                date={nextBillingDate}
                fullView
                showDay={false}
              />
            </div>
          </div>
        )}

        {isSubscribed() && billingDetails && (
          <>
            <div className="unsubscriber-button">
              <div className="auto-renew-text">
                {getTextByKey("auto-renewable-text")}
                <span
                  className={`renew-status color-${
                    userIsAutoRenew ? "green" : "red"
                  }`}
                >
                  {getTextByKey(userIsAutoRenew ? "on" : "off")}
                </span>
              </div>
              <div
                className="auto-renew-link"
                onClick={renewSubscriptionButton}
              >
                {getTextByKey(
                  isMonthly
                    ? userIsAutoRenew
                      ? "cancel-subscription"
                      : "renew-subscription"
                    : userIsAutoRenew
                    ? "disable-auto-renew"
                    : "renew-subscription"
                )}
              </div>
            </div>
            {!userIsAutoRenew && (
              <div className="auto-renew-enable-text">
                {getTextByKey("enable-auto-renewable-text")}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserSubscriptionDetailsCard;
